<template>
  <FormElement
    :rules="props.rules"
    :input-value="props.modelValue"
    :hide-details="props.hideDetails"
  >
    <VueDatePicker
      ref="datePicker"
      :model-value="props.modelValue"
      :time-picker="props.isTimePicker"
      :time-config="{
        is24: !props.isTimePicker,
        enableTimePicker: props.isTimePicker,
      }"
      :min-date="props.minDate"
      :max-date="props.maxDate"
      model-type="format"
      :formats="{ input: props.isTimePicker ? 'hh:mm a' : 'MM/dd/yy' }"
      @update:model-value="onDateInput"
    >
      <template #trigger>
        <BaseInput
          :label="props.label"
          :label-for-id="parsedId"
          :disabled="disabled"
        >
          <input
            v-bind="$attrs"
            :id="parsedId"
            :value="props.modelValue"
            :disabled="disabled"
            type="text"
            readonly
          />
          <template #append-inner>
            <Icon
              v-if="!isCloseIconDisplayed"
              :size="IconSize.LG"
              :name="props.isTimePicker ? IconName.CLOCK : IconName.CALENDAR"
              @mouseenter="isHovered = true"
              @mouseleave="isHovered = false"
            />
            <IconButton
              v-else
              :icon-name="IconName.CLOSE"
              label="Clear"
              :size="ButtonSize.XS"
              :variant="ButtonVariant.TEXT"
              :color="ButtonColor.SECONDARY"
              @mouseenter="isHovered = true"
              @mouseleave="isHovered = false"
              @click="clearValue"
            />
          </template>
        </BaseInput>
      </template>
      <template #action-select>
        <Button
          label="Select"
          :size="ButtonSize.SM"
          :variant="ButtonVariant.TEXT"
          :color="ButtonColor.SECONDARY"
          @click="selectDate"
        />
      </template>
    </VueDatePicker>
  </FormElement>
</template>

<script setup lang="ts">
import { computed, ref } from '#imports';
import { getUid } from '~/utils/common';
import { FormElement } from '~/components/inputs/_components/form-element';
import { BaseInput } from '~/components/inputs/_components/base-input';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconName,
  Icon,
  IconSize,
} from '@exfluential/ui';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

type Props = {
  modelValue: string;
  id?: string;
  label?: string;
  hideDetails?: boolean;
  rules?: [];
  clearable?: boolean;
  disabled?: boolean;
  isTimePicker?: boolean;
  minDate?: Date | string;
  maxDate?: Date | string;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string | null): void;
}>();
const isHovered = ref<boolean>(false);

const parsedId = computed<string>(() => {
  return props.id ?? `input-${getUid()}`;
});

const datePicker = ref();
const selectDate = () => {
  datePicker.value.selectDate();
};

const onDateInput = (value: string) => {
  if (value) {
    emit('update:modelValue', value);
  }
};

const clearValue = () => {
  emit('update:modelValue', null);
};

const isCloseIconDisplayed = computed<boolean>(() => {
  return Boolean(props.clearable && isHovered.value && props.modelValue);
});
</script>
